const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { timeoutPromise } = require('@/functions/functions');

class PayyoWebSocketClient {
    /**
     * @param {EventBus} eventBus
     * @param {PayyoConfig} payyoConfig
     * @param {VuexService} vuexService
     */
    constructor(
        eventBus,
        payyoConfig,
        vuexService
    ) {
        /**
         * @type {PayyoConfig}
         */
        this.config = payyoConfig;

        /**
         * @type {EventBus}
         */
        this.eventBus = eventBus;

        /**
         * @type {VuexService}
         */
        this.vuexService = vuexService;

        this.log = logFactory('PayyoWebSocketClient');

        /**
         * @private
         *
         * @type {WebSocket}
         */
        this.socket = null;

        /**
         * Is the WebSocket connected?
         * true = connected, false = disconnected, null = in progress.
         *
         * @type {boolean|null}
         */
        this.connected = false;

        /**
         * Has the disconnect() method been called?
         * If true, the WebSocket will not attempt to reconnect when it closes.
         * If false, the WebSocket will attempt to reconnect when it closes unexpectedly.
         * This is reset to false when the socket successfully disconnects.
         *
         * @type {boolean}
         */
        this.disconnectRequested = false;

        /**
         * @type {Promise}
         */
        this.connectPromise = null;

        /**
         * The ping interval in ms or null when the ping interval is not set.
         *
         * @type {number|null}
         */
        this.pingInterval = null;

        /**
         * This is the callback that will be called when a message is received from the WebSocket.
         */
        this.onMessage = () => {};
    }

    /**
     * @param {PayyoConfig} payyoConfig
     */
    async setConfig(payyoConfig) {
        this.config = payyoConfig;

        if (this.connected) {
            await this.disconnect();
            await timeoutPromise(1000);
            await this.connect();
        }
    }

    /**
     * @returns {Promise<WebSocket>}
     */
    connect() {
        try {
            this.config.validate();
        } catch (err) {
            throw new Error(`Invalid Payyo configuration: ${err.message}`);
        }

        if (this.connectPromise && this.connected !== false) {
            this.log.debug('Reusing existing WebSocket connection', this.socket);
            return this.connectPromise;
        }

        this.setConnected(null);
        this.connectPromise = new Promise((resolve, reject) => {
            this.log.debug('Connecting Websocket...');

            let url = new URL(this.config.apiSocketUrl + this.config.apiUrlPath + '/');
            url.searchParams.append('device_id', this.config.deviceId);
            url.searchParams.append('authorization', this.config.apiKey);

            this.socket = new WebSocket(url);

            this.socket.onopen = (event) => {
                this.log.debug('WebSocket opened', event);
                this.setConnected(true);

                // Broadcast the connected event so that other parts of the app can respond.
                this.eventBus.broadcast(EventNames.PAYYO_WEB_SOCKET_CONNECTED, event);

                resolve(this.socket);
            };

            this.socket.onerror = (event) => {
                this.log.error('WebSocket error', event);

                // Broadcast the error event so that other parts of the app can respond.
                this.eventBus.broadcast(EventNames.PAYYO_WEB_SOCKET_ERROR, event);

                reject(new Error(`WebSocket error: ${event.message}`));
            };

            this.socket.onmessage = (message) => {
                this.log.debug('WebSocket message', message);
                this.onMessage(message);
            };

            this.socket.onclose = async (event) => {
                this.log.warn('WebSocket closed', event);
                this.setConnected(false);

                if (this.disconnectRequested) {
                    // We were intentionally disconnectRequested, so don't try to reconnect.
                    this.disconnectRequested = false;
                    return;
                }

                // The socket closed unexpectedly, so try to reconnect automatically.
                this.log.warn('WebSocket closed unexpectedly, attempting to reconnect...');
                // Wait a moment before reconnecting.
                await timeoutPromise(1000);
                this.connect();
            };
        });

        return this.connectPromise;
    }

    disconnect() {
        this.disconnectRequested = true;
        if (this.socket) {
            this.socket.close();
        }
    }

    async getDeviceStatus() {
        if (this.socket) {
            this.sendMessage('device.status');
        }
    }

    sendMessage(action) {
        const message = JSON.stringify({ action });
        this.log.debug('Sending message', message);
        this.socket.send(message);
    }

    /**
     * @param {boolean|null} connected
     */
    setConnected(connected) {
        this.connected = connected;
        this.vuexService.commit('payment/setPayyoWebSocketConnected', connected);

        if (connected) {
            this.startPingInterval();
        } else {
            this.stopPingInterval();
        }
    }

    /**
     * Sends a message over the websocket every 50 seconds to keep the connection alive.
     */
    startPingInterval() {
        this.stopPingInterval();

        this.pingInterval = setInterval(
            () => {
                if (this.connected) {
                    // New ping action added in August 2025:
                    // https://docs.pos.payyo.ch/docs/notifications/websockets/#application-level-heartbeat
                    // This is designed to be called from a browser to keep the connection alive.
                    this.sendMessage('ping');
                }
            },
            50000
        );
    }

    stopPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }
}

module.exports = PayyoWebSocketClient;
